<template>
  <div
    v-if="show"
    data-testid="timers-overlay"
    class="timers-overlay player-debug-overlay-surface player-debug-overlay-legible z-40 flex min-w-[180px] max-w-[min(240px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border border-white/15 text-white/90 shadow-2xl shadow-black/70 ring-1 ring-black/30 xl:min-w-[200px]"
    :class="containerClasses"
  >
    <div class="border-b border-white/15 bg-white/[0.03] px-3 py-2">
      <div class="flex items-center justify-between gap-2">
        <span class="text-[11px] font-semibold tracking-wide text-white">Timers</span>
        <div class="flex items-center gap-1.5">
          <span
            v-if="cachedConfigBadge != null"
            data-testid="timers-cached-config-badge"
            class="rounded-full border border-amber-400/40 bg-amber-500/25 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-200"
          >
            {{ cachedConfigBadge }}
          </span>
          <span
            v-if="timerStuckSuspected"
            data-testid="timers-stuck-badge"
            class="timers-counter rounded-full border border-amber-400/40 bg-amber-500/25 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-amber-200"
          >
            Stuck +{{ timerStuckOverrunSec }}s
          </span>
        </div>
      </div>
    </div>

    <div class="flex min-w-0 flex-col gap-0.5 px-3 py-2 text-[10px] leading-tight">
      <div class="info-row">
        <span class="info-label">Campaign</span>
        <span data-testid="timers-campaign-line" class="timers-counter">{{ campaignLine }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Frame</span>
        <span data-testid="timers-frame-line" class="timers-counter">{{ frameLine }}</span>
      </div>
      <div class="info-row info-row-nowrap">
        <span class="info-label">Last frame</span>
        <span data-testid="timers-last-frame-line" class="timers-counter">{{ lastFrameLine }}</span>
      </div>
      <div class="info-row info-row-nowrap">
        <span class="info-label">Widget</span>
        <span data-testid="timers-widget-line" class="timers-counter">{{ widgetLine }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, toRefs, unref, watch } from 'vue'
import usePlayer from '@/composables/usePlayer'
import { cssEffectiveScreenRotationFromState } from '@/helpers/cssEffectiveScreenRotation'
import { extrapolatePlaybackTimingOverlay } from '@/helpers/extrapolatePlaybackTimingOverlay'
import { formatMs } from '@/helpers/formatMs'
import { formatRegistrationSnapshotAge } from '@/helpers/formatRegistrationSnapshotAge'
import { getCornerPositionClasses } from '@/helpers/getCornerPositionClasses'

/** Fallback when the tab is hidden and rAF is throttled. */
const OVERLAY_CLOCK_HIDDEN_TICK_MS = 250

const { state } = usePlayer()
const {
  showTimersOverlay,
  playbackTiming,
  playbackTimingSampleAtPerfMs,
  playbackTimerStuck,
  widgetRevealMs,
  widgetRevealTimedOut,
  currentStateCorner,
  registrationConfigurationSource,
  registrationSnapshotSavedAtMs
} = toRefs(state)

const show = computed(() => unref(showTimersOverlay))

const timerStuckSuspected = computed(() => unref(playbackTimerStuck)?.suspected === true)

const cachedConfigBadge = computed((): string | null => {
  if (unref(registrationConfigurationSource) !== 'cached') {
    return null
  }
  const age = formatRegistrationSnapshotAge(unref(registrationSnapshotSavedAtMs))
  return age != null ? `Cached ${age}` : 'Cached'
})

const timerStuckOverrunSec = computed(() => {
  const ms = unref(playbackTimerStuck)?.overrunMs ?? 0
  return ms > 0 ? (ms / 1000).toFixed(1) : '0.0'
})

/** Opposite corner from status panel so the two don't overlap. */
const containerClasses = computed(() =>
  getCornerPositionClasses(
    (unref(currentStateCorner) + 2) % 4,
    cssEffectiveScreenRotationFromState(state)
  )
)

const overlayNowPerfMs = ref(performance.now())
let overlayClockRafId: number | null = null
let overlayClockTimer: ReturnType<typeof setInterval> | null = null

function stopOverlayClock(): void {
  if (overlayClockRafId != null) {
    cancelAnimationFrame(overlayClockRafId)
    overlayClockRafId = null
  }
  if (overlayClockTimer != null) {
    clearInterval(overlayClockTimer)
    overlayClockTimer = null
  }
}

function startOverlayClock(): void {
  stopOverlayClock()
  overlayNowPerfMs.value = performance.now()
  /** rAF tracks wall time whenever the UI thread is painting — smoother on low-end Android than 250ms setInterval. */
  const loop = () => {
    overlayNowPerfMs.value = performance.now()
    overlayClockRafId = requestAnimationFrame(loop)
  }
  overlayClockRafId = requestAnimationFrame(loop)
  /** Hidden tabs throttle rAF; keep a slow interval so extrapolation still advances. */
  overlayClockTimer = setInterval(() => {
    if (document.visibilityState === 'hidden') {
      overlayNowPerfMs.value = performance.now()
    }
  }, OVERLAY_CLOCK_HIDDEN_TICK_MS)
}

watch(
  show,
  (visible) => {
    if (visible) {
      startOverlayClock()
    }
    else {
      stopOverlayClock()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  stopOverlayClock()
})

const displayTiming = computed(() => {
  const t = unref(playbackTiming)
  if (!t) {
    return null
  }
  const sampleAt = unref(playbackTimingSampleAtPerfMs)
  if (sampleAt == null) {
    return t
  }
  const extrapolated = extrapolatePlaybackTimingOverlay({
    frameElapsedMs: t.frameElapsedMs,
    frameDurationMs: t.frameDurationMs,
    playlistElapsedMs: t.playlistElapsedMs,
    playlistDurationMs: t.playlistDurationMs,
    sampleAtPerfMs: sampleAt,
    nowPerfMs: unref(overlayNowPerfMs),
    freeze: unref(timerStuckSuspected)
  })
  return {
    ...t,
    frameElapsedMs: extrapolated.frameElapsedMs,
    playlistElapsedMs: extrapolated.playlistElapsedMs
  }
})

const campaignLine = computed(() => {
  const t = unref(displayTiming)
  if (!t) { return '—' }
  return `${formatMs(t.playlistElapsedMs)} / ${formatMs(t.playlistDurationMs)}`
})

const frameLine = computed(() => {
  const t = unref(displayTiming)
  if (!t) { return '—' }
  return `${formatMs(t.frameElapsedMs)} / ${formatMs(t.frameDurationMs)}`
})

const lastFrameLine = computed(() => {
  const t = unref(playbackTiming)
  if (!t) { return '—' }
  return formatMs(t.lastFrameDurationMs)
})

/** Ms until current widget was shown; "(timeout)" when promoted after ready timeout. */
const widgetLine = computed(() => {
  const ms = unref(widgetRevealMs)
  if (ms == null) { return '—' }
  const sec = (ms / 1000).toFixed(1)
  return unref(widgetRevealTimedOut) ? `${sec}s (timeout)` : `${sec}s`
})
</script>

<style scoped>
@reference "../assets/main.css";

.info-row {
  @apply flex min-w-0 items-center gap-1.5;
}
.info-row-nowrap {
  @apply whitespace-nowrap;
}
.info-label {
  /* "Last frame" needs a bit more than status PT labels at 10px */
  @apply w-[6.5em] shrink-0 font-medium text-white/[0.55] xl:w-[7em];
}
.info-row > :not(.info-label) {
  @apply min-w-0;
}
/**
 * Counter values must stay monospace (labels stay Inter). Prefer `--font-mono`
 * from Tailwind theme; fall back to a system mono stack so Android WebView
 * cannot inherit the panel/root sans face when utility classes are stripped.
 */
.timers-counter {
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace);
  font-variant-numeric: tabular-nums;
}
</style>
